import { getCommonAnalytics } from './common-analytics';

export interface AnalyticsContext {
  element: Element;
  event: Event;
  data: Record<string, unknown>;
  context?: unknown;
}

type AnalyticsResolver<T> = T | ((context: AnalyticsContext) => T);
type AnalyticsValue = AnalyticsResolver<unknown>;
type AnalyticsOperation = ((context: AnalyticsContext) => void) & { operationName?: string };
type AnalyticsMatcher = string | ((context: AnalyticsContext) => boolean);

interface AnalyticsOperations<Stage> {
  collectCommon(): Stage;
  set<T>(key: string, value: AnalyticsResolver<T>): Stage;
  set<T extends Record<string, unknown>>(values: {
    [K in keyof T]: AnalyticsResolver<T[K]>;
  }): Stage;
  unset(...keys: string[]): Stage;
  enrich(enricher: (context: AnalyticsContext) => Record<string, unknown> | void): Stage;
  extendWhen(matcher: AnalyticsMatcher, configure: (extension: AnalyticsExtension) => void): Stage;
}

export interface AnalyticsExtension extends AnalyticsOperations<AnalyticsExtension> {
  readonly __analyticsExtensionBrand?: never;
}

export interface AnalyticsRule {
  name(name: string): AnalyticsNamedRule;
}

export interface AnalyticsNamedRule extends AnalyticsOperations<AnalyticsNamedRule> {
  on(eventName: string): AnalyticsBoundRule;
}

export interface AnalyticsBoundRule extends AnalyticsOperations<AnalyticsBoundRule> {
  emit(eventName?: string): void;
}

interface RegisteredRule {
  selector: string;
  sourceEvent: string;
  name: string;
  operations: readonly AnalyticsOperation[];
  emittedEventName?: string;
}

const rulesByEvent = new Map<string, RegisteredRule[]>();
const registeredListeners = new Set<string>();
const captureEvents = new Set(['play', 'pause', 'ended']);
const pageLoadEvents = new Set(['DOMContentLoaded']);

function diagnostic(code: string, details: Record<string, unknown>): void {
  console.warn(code, details);
}

function failedOperation(rule: RegisteredRule, operation: string, error: unknown): void {
  diagnostic('ANALYTICS_OPERATION_FAILED', { rule: rule.name, operation, error });
}

function resolveValue(value: AnalyticsValue, context: AnalyticsContext): unknown {
  return typeof value === 'function' ? value(context) : value;
}

function addOperation(
  operations: AnalyticsOperation[],
  operationName: string,
  operation: (context: AnalyticsContext) => void
): void {
  const namedOperation = operation as AnalyticsOperation;
  namedOperation.operationName = operationName;
  operations.push(namedOperation);
}

function addExtensionOperations(operations: AnalyticsOperation[]): AnalyticsExtension {
  const extension: AnalyticsExtension = {
    collectCommon() {
      addOperation(operations, 'collectCommon', ({ element, data }) => {
        Object.assign(data, getCommonAnalytics(element));
      });
      return extension;
    },
    set<T>(keyOrValues: string | { [K in keyof T]: AnalyticsResolver<T[K]> }, value?: AnalyticsResolver<T>) {
      if (typeof keyOrValues === 'string') {
        addOperation(operations, 'set', (context) => {
          context.data[keyOrValues] = resolveValue(value, context);
        });
      } else {
        addOperation(operations, 'set', (context) => {
          const values = keyOrValues as Record<string, AnalyticsValue>;
          Object.keys(values).forEach((key) => {
            context.data[key] = resolveValue(values[key], context);
          });
        });
      }
      return extension;
    },
    unset(...keys: string[]) {
      addOperation(operations, 'unset', ({ data }) => {
        keys.forEach((key) => delete data[key]);
      });
      return extension;
    },
    enrich(enricher) {
      addOperation(operations, 'enrich', (context) => {
        const additionalData = enricher(context);
        if (additionalData) Object.assign(context.data, additionalData);
      });
      return extension;
    },
    extendWhen(matcher, configure) {
      const extensionOperations: AnalyticsOperation[] = [];
      configure(addExtensionOperations(extensionOperations));
      const configuredOperations = Object.freeze(extensionOperations.slice());
      addOperation(operations, 'extendWhen', (context) => {
        const matches = typeof matcher === 'string' ? context.element.matches(matcher) : matcher(context);
        if (matches) configuredOperations.forEach((operation) => operation(context));
      });
      return extension;
    },
  };
  return extension;
}

function validSelector(selector: string): boolean {
  if (!selector.trim()) return false;
  try {
    document.createElement('div').matches(selector);
    return true;
  } catch {
    return false;
  }
}

function computeMatches(rule: RegisteredRule, event: Event): Element[] {
  // For page Load events - e.g DOMContentLoaded. We will get all the items defined via rule.selector
  if (pageLoadEvents.has(rule.sourceEvent)) {
    try {
      return Array.from(document.querySelectorAll(rule.selector));
    } catch (error) {
      diagnostic('INVALID_RULE', { rule: rule.name, selector: rule.selector, error });
      return [];
    }
  }

  const path = event.composedPath().filter((entry): entry is Element => entry instanceof Element);
  const matches: Element[] = [];
  path.forEach((element) => {
    try {
      if (element.matches(rule.selector)) matches.push(element);
    } catch (error) {
      diagnostic('INVALID_RULE', { rule: rule.name, selector: rule.selector, error });
    }
  });
  return matches;
}

function dispatch(sourceEvent: string, event: Event): void {
  const rules = rulesByEvent.get(sourceEvent) ?? [];
  const emittedByTarget = new Map<Element, Map<string, RegisteredRule>>();

  rules.forEach((rule) => {
    const matches = computeMatches(rule, event);
    
    if (matches.length > 1 && !pageLoadEvents.has(sourceEvent)) {
      diagnostic('MULTIPLE_TARGETS', { rule: rule.name, matchCount: matches.length });
    }

    matches.forEach((element) => {
      const emittedEventName = rule.emittedEventName ?? sourceEvent;
      const data: Record<string, unknown> = {};
      const context: AnalyticsContext = { element, event, data };
      let operationName = 'unknown';

      try {
        rule.operations.forEach((operation) => {
          operationName = operation.operationName ?? operationName;
          operation(context);
        });

        operationName = 'emit';
        (window as any).Mhecomm?.emit(emittedEventName, data, {}, element);

        const targets = emittedByTarget.get(element) ?? new Map<string, RegisteredRule>();
        const previousRule = targets.get(emittedEventName);
        if (previousRule && previousRule !== rule) {
          diagnostic('POSSIBLE_DUPLICATE_EMISSION', {
            element,
            emittedEventName,
            rules: [previousRule.name, rule.name],
          });
        }
        targets.set(emittedEventName, rule);
        emittedByTarget.set(element, targets);
      } catch (error) {
        failedOperation(rule, operationName, error);
      }
    });
  });
}

function register(rule: RegisteredRule): void {
  const rules = rulesByEvent.get(rule.sourceEvent) ?? [];
  rules.push(rule);
  rulesByEvent.set(rule.sourceEvent, rules);
  if (!registeredListeners.has(rule.sourceEvent)) {
    document.addEventListener(
      rule.sourceEvent,
      (event) => dispatch(rule.sourceEvent, event),
      captureEvents.has(rule.sourceEvent)
    );
    registeredListeners.add(rule.sourceEvent);
  }
}

class RuleBuilder {
  private readonly operations: AnalyticsOperation[] = [];
  private ruleName?: string;
  private sourceEvent?: string;
  private sealed = false;

  constructor(private readonly selector: string) {}

  name(name: string): AnalyticsNamedRule {
    if (!this.canConfigure('name')) return this as unknown as AnalyticsNamedRule;
    this.ruleName = name;
    return this as unknown as AnalyticsNamedRule;
  }

  on(eventName: string): AnalyticsBoundRule {
    if (!this.canConfigure('on')) return this as unknown as AnalyticsBoundRule;
    this.sourceEvent = eventName;
    return this as unknown as AnalyticsBoundRule;
  }

  collectCommon(): AnalyticsBoundRule {
    this.addConfiguredOperation('collectCommon');
    return this as unknown as AnalyticsBoundRule;
  }

  set<T>(key: string, value: AnalyticsResolver<T>): AnalyticsBoundRule;
  set<T extends Record<string, unknown>>(values: { [K in keyof T]: AnalyticsResolver<T[K]> }): AnalyticsBoundRule;
  set<T>(
    keyOrValues: string | { [K in keyof T]: AnalyticsResolver<T[K]> },
    value?: AnalyticsResolver<T>
  ): AnalyticsBoundRule {
    this.addConfiguredOperation('set', keyOrValues, value);
    return this as unknown as AnalyticsBoundRule;
  }

  unset(...keys: string[]): AnalyticsBoundRule {
    this.addConfiguredOperation('unset', keys);
    return this as unknown as AnalyticsBoundRule;
  }

  enrich(enricher: (context: AnalyticsContext) => Record<string, unknown> | void): AnalyticsBoundRule {
    this.addConfiguredOperation('enrich', enricher);
    return this as unknown as AnalyticsBoundRule;
  }

  extendWhen(matcher: AnalyticsMatcher, configure: (extension: AnalyticsExtension) => void): AnalyticsBoundRule {
    this.addConfiguredOperation('extendWhen', matcher, configure);
    return this as unknown as AnalyticsBoundRule;
  }

  emit(eventName?: string): void {
    if (this.sealed) {
      diagnostic('INVALID_RULE', { reason: 'Rule is already sealed', selector: this.selector });
      return;
    }
    this.sealed = true;
    if (!this.ruleName || !this.sourceEvent || !validSelector(this.selector)) {
      diagnostic('INVALID_RULE', {
        name: this.ruleName,
        selector: this.selector,
        sourceEvent: this.sourceEvent,
      });
      return;
    }
    register({
      selector: this.selector,
      sourceEvent: this.sourceEvent,
      name: this.ruleName,
      operations: Object.freeze(this.operations.slice()),
      emittedEventName: eventName,
    });
  }

  private canConfigure(operation: string): boolean {
    if (this.sealed) {
      diagnostic('INVALID_RULE', { reason: 'Rule is already sealed', operation, selector: this.selector });
      return false;
    }
    return true;
  }

  private addConfiguredOperation(operationName: string, ...args: unknown[]): void {
    if (!this.canConfigure(operationName)) return;
    const extensionOperations: AnalyticsOperation[] = [];
    const extension = addExtensionOperations(extensionOperations);
    try {
      if (operationName === 'collectCommon') extension.collectCommon();
      if (operationName === 'set') {
        if (typeof args[0] === 'string') {
          extension.set(args[0], args[1] as AnalyticsResolver<unknown>);
        } else {
          extension.set(args[0] as Record<string, AnalyticsResolver<unknown>>);
        }
      }
      if (operationName === 'unset') extension.unset(...(args[0] as string[]));
      if (operationName === 'enrich') {
        extension.enrich(args[0] as (context: AnalyticsContext) => Record<string, unknown> | void);
      }
      if (operationName === 'extendWhen') {
        extension.extendWhen(args[0] as AnalyticsMatcher, args[1] as (extension: AnalyticsExtension) => void);
      }
      this.operations.push(...extensionOperations);
    } catch (error) {
      diagnostic('INVALID_RULE', { operation: operationName, selector: this.selector, error });
    }
  }
}

export function analytics(selector: string): AnalyticsRule {
  return new RuleBuilder(selector);
}
